import { computed } from 'vue';
import type { Router } from 'vue-router';
import { formatDateForApi, parseCivilDate } from '@/shared/lib';
import {
  trainingApi,
  type CreateOnboardingTrainingPayload,
  type CreateOnboardingTrainingSessionPayload,
  type TrainingDetail,
} from '@/entities/Training';
import { useOnboardingMinistrantes, type OnboardingMinistranteRow } from './useOnboardingMinistrantes';
import { useOnboardingDocuments } from './useOnboardingDocuments';
import { useTrainingFormResources } from './useTrainingFormResources';
import { useTrainingFormFields } from './useTrainingFormFields';
import { useTrainingFormSubmit } from './useTrainingFormSubmit';
import { useScheduleAndTraineeEditing, buildScheduleSnapshotFields } from './useScheduleAndTraineeEditing';
import { getMemberPositions } from '../lib/getMemberPositions';
import { buildMinistranteSessionPayload } from '../lib/buildMinistranteSessionPayload';

export { getMemberPositions };

export interface UseOnboardingFormEditContext {
  trainingId: number;
  initial: TrainingDetail;
  /**
   * Captured synchronously by the edit dispatcher before its await, since this composable
   * is instantiated after one — useRouter() would silently resolve to undefined by then.
   */
  router?: Router;
}

function buildInitialMinistranteRows(initial: TrainingDetail): OnboardingMinistranteRow[] {
  return initial.instructors.map((instructor) => ({
    id: instructor.sessionId,
    responsibleMemberId: instructor.memberId,
    responsibleUserPositionId: instructor.position?.id ?? null,
    trainingDate: parseCivilDate(instructor.trainingDate),
  }));
}

export function useOnboardingForm(editContext?: UseOnboardingFormEditContext) {
  const fields = useTrainingFormFields();
  const {
    title,
    notes,
    scheduledByMemberId,
    scheduledByPositionId,
    scheduledDate,
    traineeMemberId,
    traineePositionId,
    trainingMatrixRevisionId,
  } = fields;

  const resources = useTrainingFormResources();
  const { members, revisions, loading, memberOptions } = resources;

  const ministrantes = useOnboardingMinistrantes(
    members,
    editContext ? buildInitialMinistranteRows(editContext.initial) : undefined
  );

  const documents = useOnboardingDocuments(traineePositionId, trainingMatrixRevisionId);

  const { saving, submit: submitTraining } = useTrainingFormSubmit(editContext?.router);

  const { load, setScheduledByMemberId, setTraineeMemberId } = useScheduleAndTraineeEditing(
    fields,
    resources,
    editContext
  );

  const scheduledByPositions = computed(() => getMemberPositions(members.value, scheduledByMemberId.value));
  const traineePositions = computed(() => getMemberPositions(members.value, traineeMemberId.value));

  const canSave = computed(() => {
    return (
      title.value.trim() !== '' &&
      scheduledByMemberId.value !== null &&
      scheduledByPositionId.value !== null &&
      scheduledDate.value !== null &&
      traineeMemberId.value !== null &&
      traineePositionId.value !== null &&
      trainingMatrixRevisionId.value !== null &&
      ministrantes.isComplete.value
    );
  });

  function buildSubmitPayload(): CreateOnboardingTrainingPayload | null {
    if (
      scheduledByMemberId.value === null ||
      scheduledByPositionId.value === null ||
      scheduledDate.value === null ||
      traineeMemberId.value === null ||
      traineePositionId.value === null ||
      trainingMatrixRevisionId.value === null
    ) {
      return null;
    }

    const apiScheduledDate = formatDateForApi(scheduledDate.value);
    if (apiScheduledDate === null) return null;

    const sessions: CreateOnboardingTrainingSessionPayload[] = [];
    for (const row of ministrantes.rows.value) {
      const session = buildMinistranteSessionPayload(row);
      if (session === null) return null;
      sessions.push(session);
    }

    return {
      title: title.value.trim(),
      notes: notes.value.trim() ? notes.value.trim() : undefined,
      scheduledBy: { memberId: scheduledByMemberId.value, positionId: scheduledByPositionId.value },
      plannedDate: apiScheduledDate,
      trainees: [{ memberId: traineeMemberId.value, positionId: traineePositionId.value }],
      trainingMatrixRevisionId: trainingMatrixRevisionId.value,
      sessions,
    };
  }

  async function submit(onSuccess?: () => void): Promise<void> {
    if (editContext) {
      await submitTraining(
        canSave.value,
        buildSubmitPayload,
        (payload) => trainingApi.updateOnboardingTraining(editContext.trainingId, payload),
        { mode: 'edit', onSuccess }
      );
      return;
    }

    await submitTraining(canSave.value, buildSubmitPayload, (payload) =>
      trainingApi.createOnboardingTraining(payload)
    );
  }

  const snapshot = computed(() => JSON.stringify(buildScheduleSnapshotFields(fields, ministrantes)));

  return {
    title,
    notes,
    scheduledByMemberId,
    scheduledByPositionId,
    scheduledDate,
    traineeMemberId,
    traineePositionId,
    trainingMatrixRevisionId,
    members,
    revisions,
    scheduledByPositions,
    traineePositions,
    memberOptions,
    loading,
    saving,
    canSave,
    ministrantes,
    documents,
    snapshot,
    load,
    setScheduledByMemberId,
    setTraineeMemberId,
    submit,
  };
}
